import { type FC, useEffect, useRef } from 'react'

/** window.turnstile（Cloudflare Turnstile スクリプトが注入する API）の最小型 */
interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback'?: () => void
    },
  ) => string
  remove: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

/**
 * Cloudflare Turnstile ウィジェット。
 *
 * - `VITE_TURNSTILE_SITE_KEY` が設定されていればスクリプトを読み込みウィジェットを描画し、
 *   検証トークンを `onToken` で親へ渡す。
 * - site key が未設定（ローカル/開発）の場合は何も描画せず no-op。captcha_token は "" のまま送る。
 *
 * これにより site key が無い環境でもフォームが hard-fail しない。
 */
export const TurnstileWidget: FC<{ onToken: (token: string) => void }> = ({ onToken }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

  useEffect(() => {
    if (!siteKey || typeof window === 'undefined') return

    let widgetId: string | undefined
    let cancelled = false

    const renderWidget = () => {
      if (cancelled || !containerRef.current || !window.turnstile) return
      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: token => onToken(token),
        'expired-callback': () => onToken(''),
      })
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (window.turnstile) {
      renderWidget()
    } else if (existing) {
      existing.addEventListener('load', renderWidget)
    } else {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.addEventListener('load', renderWidget)
      document.head.appendChild(script)
    }

    return () => {
      cancelled = true
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId)
      }
    }
  }, [siteKey, onToken])

  if (!siteKey) return null

  return <div ref={containerRef} className="my-2" data-testid="turnstile-widget" />
}
